/* eslint-disable max-len */
/* eslint-disable no-console */
import React, { lazy, Suspense, useState, useEffect, useRef, useCallback } from 'react';
import { pdf } from '@react-pdf/renderer';
import { LinearProgress, Typography, Modal, Backdrop, CircularProgress, Box, Grid, Tooltip } from '@mui/material';
import { Canvg } from 'canvg';
import { Rnd } from 'react-rnd';
import ArgoReport from '../ARGO_HeatMapChart';
import { closeIconBlack, download_feed } from '../../shared/assets/images';

// Lazy load the ArgoHeatMapChart component
const ArgoHeatMapChart = lazy(() => import('./argo_heatmap_Chart'));

// Memoized ChartComponent to prevent unnecessary re-renders
const ChartComponent = React.memo(({ element, lineChartData, onSvgReady }) => (
  <ArgoHeatMapChart product={element} lineChartData={lineChartData[element]} onSvgReady={onSvgReady} />
));

// LinearProgressWithLabel component to show progress with a label
function LinearProgressWithLabel(props) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress variant="determinate" {...props} style={{ color: '#002D62' }} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

// Main ArgoDashboard component
function ArgoDashboard(props) {
  const { isShowArgoCompare, handleCloseIsShowArgoCompare, data, argoProducts, translateObject } = props;
  const [progress, setProgress] = React.useState(0);
  const [visibleCharts, setVisibleCharts] = useState(2);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [pdfButton, setPdfButton] = useState(false);
  const contentGridRef = useRef(null);

  // Effect to handle the loading of charts
  useEffect(() => {
    const interval = setInterval(() => {
      setFetchLoading(false);
      setVisibleCharts(prev => {
        if (prev < argoProducts.length) {
          setProgress((prevProgress) => (prevProgress >= 100 ? 100 : (prev + 1) * (100 / argoProducts.length)));
          return prev + 1;
        }
        setFetchLoading(false);
        setPdfButton(true);
        clearInterval(interval);
        return prev;
      });
    }, 1500); // Adjust the delay as needed
  }, [argoProducts.length]);

  const canvasRefs = useRef([]);
  const [svgData, setSvgData] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Function to draw SVG on canvases
  const drawSVGOnCanvases = () => {
    const canvasData = [];
    svgData.forEach((svg, index) => {
      const canvas = canvasRefs.current[index];
      const ctx = canvas.getContext('2d');
      if (svg.startsWith('<svg')) {
        const v = Canvg.fromString(ctx, svg);
        v.start();
        canvasData.push(canvas.toDataURL('image/png'));
      } else {
        console.error('Invalid SVG data:', svg);
      }
    });
    return canvasData;
  };

  // Callback to handle SVG data
  const handleSvgData = useCallback((svg, index) => {
    setSvgData(prevData => {
      const newData = [...prevData];
      newData[index] = svg;
      return newData;
    });
  }, []);

  // Function to generate PDF
  const generatePDF = async () => {
    setPdfLoading(true);
    try {
      const svgData = drawSVGOnCanvases();
      const blob = await pdf(<ArgoReport svgData={svgData} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'ArgoReport.pdf');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setPdfLoading(false);
    }
  };

  const lineChartData = data?.as_line_graph || [];

  return (
    <Modal
      hideBackdrop
      open={isShowArgoCompare}
      onClose={handleCloseIsShowArgoCompare}
      aria-labelledby="child-modal-title"
      aria-describedby="child-modal-description"
      className="argoComp-modal"
    >
      <Box className=''>
        <Rnd
          default={{
            width: '90%',
            x: ((window.innerWidth / 2) - ((90 * (window.innerWidth / 100)) / 2)),
            y: ((window.innerHeight / 2) - ((70 * (window.innerHeight / 100)) / 2)),
          }}
          enableResizing={{
            bottom: false, bottomLeft: false, bottomRight: false,
            left: true, right: true,
            top: false, topLeft: false, topRight: false
          }}
          allowAnyClick={false}
          enableUserSelectHack={false}
          resizeHandleStyles={{ 'right': { 'width': '5px' } }} // reducing width of right handle to 5px ---> kranthi
          minWidth='47%'
          cancel=".ro-table-grid"
          onDragStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          className='argoComp-box'>
          <Grid container className='argoComp-grid-container'>
            <Backdrop
              sx={{ color: '#fff', zIndex: 999999 }}
              style={{
                background: '#d9dbde5c',
              }}
              open={fetchLoading || pdfLoading}>
              <CircularProgress color="inherit" style={{ color: '#4b93f4' }} />
            </Backdrop>
            <Grid item xs={12}>
              <Grid item xs={12} className='model_header'>
                <div className='model_header_text S_heading'>{translateObject('Saturation Index Heat Map')}</div>
                <div className='argoComp-close-icon'>
                  {pdfButton && <Tooltip title="Download as PDF" placement="top" data-testid="download-csv" arrow>
                    <img
                      src={download_feed}
                      alt="close"
                      onClick={generatePDF}
                      style={{ width: '24px', padding: '7px', height: '24px' }}
                      className="pointer"
                    />
                  </Tooltip>}
                  <Tooltip title="Close">
                    <img
                      src={closeIconBlack}
                      onClick={handleCloseIsShowArgoCompare}
                      style={{ width: '14px', padding: '11px' }}
                      className="pointer"
                    />
                  </Tooltip>
                </div>
              </Grid>
              <Suspense fallback={<CircularProgress color="inherit" style={{ color: '#4b93f4' }} />}>
                {progress !== 100 && <LinearProgressWithLabel value={progress} />}
                <Grid item xs={12} className='argoComp-content-grid'
                  ref={contentGridRef} style={{ overflow: 'auto', 'height': '500px', display: 'flex', flexWrap: 'wrap' }}>
                  {argoProducts.slice(0, visibleCharts).map((element, i) => (
                    lineChartData[element] && <ChartComponent key={i} element={element} lineChartData={lineChartData}
                      onSvgReady={(svg) => handleSvgData(svg, i)} />
                  ))}
                  {argoProducts.slice(0, visibleCharts).map((_, i) => (
                    <canvas key={i} ref={el => { canvasRefs.current[i] = el; }} style={{ display: 'none' }} width="100" height="500" />
                  ))}
                </Grid>
              </Suspense>
            </Grid>
          </Grid>
        </Rnd>
      </Box>
    </Modal>
  );
}

export default ArgoDashboard;